import { Loader2, OctagonX } from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import { selectConsensusGauge } from "../../store/selectors";
import { ConsensusMeter } from "./ConsensusMeter";
import { Pill } from "../common/Pill";
import { cn } from "../common/cn";
import { t } from "../../i18n";

interface ModeratorBarProps {
  className?: string;
}

function RoundProgress({ round, max }: { round: number; max: number }) {
  return (
    <div className="flex items-center gap-1" aria-hidden>
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < round;
        const current = i === round - 1;
        return (
          <span
            key={i}
            className={cn(
              "block h-2 rounded-full transition-all duration-300",
              filled ? "w-5 bg-[hsl(var(--accent))]" : "w-2 bg-white/[0.08]",
              current && "shadow-[0_0_6px_hsl(var(--accent)/0.5)]",
            )}
          />
        );
      })}
    </div>
  );
}

export function ModeratorBar({ className }: ModeratorBarProps) {
  const { round, pending, deadlock } = useDebateStore(selectConsensusGauge);
  const maxRounds = useDebateStore((s) => s.maxRounds);

  return (
    <div
      data-moderator-bar
      className={cn(
        "flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-[hsl(220_22%_6%/0.9)] px-4 py-3 backdrop-blur-lg shadow-[0_4px_24px_-8px_rgb(0_0_0/0.4)]",
        className,
      )}
      aria-label={t("moderator.title")}
    >
      {/* Radial gauge */}
      <ConsensusMeter />

      {/* Round progress */}
      <div className="flex flex-1 flex-col gap-1.5 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
            {t("moderator.roundLabel")}
          </span>
          <span className="font-mono text-sm tabular-nums text-fg-strong">
            {round}<span className="text-fg-subtle/70">/{maxRounds}</span>
          </span>
        </div>
        <RoundProgress round={round} max={maxRounds} />
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {pending ? (
          <Pill tone="info" icon={<Loader2 className="h-3 w-3 animate-spin" aria-hidden />}>
            {t("moderator.consensusPending")}
          </Pill>
        ) : null}
        {deadlock ? (
          <Pill tone="danger" icon={<OctagonX className="h-3 w-3" aria-hidden />}>
            {t("moderator.deadlock")}
          </Pill>
        ) : null}
      </div>
    </div>
  );
}
